import { getChosenPropsMutationXml } from './BlocklifyUtil'
import _ from 'lodash'

//
// Interceptors are syntax handlers for specific high level blocks such as 'print' 'new guid' etc.
// They are allowed to run before the base language syntax handlers
//
export default function(registerInterceptor) {
  registerInterceptor('MemberExpression', (code, node, walk) => {
    // A map to reduce boilerplate for api property accessors
    const map = {
      Fs: { tmpFolder: 'sirenia_api_fs_tmpfolder' },
      Processes: { current: 'sirenia_api_processes_current' },
      Windows: { primary: 'sirenia_api_windows_primary' },
      Env: {
        userName: 'sirenia_api_env_username',
        machineName: 'sirenia_api_env_machinename',
        userDomain: 'sirenia_api_env_userdomain',
        primaryScreen: 'sirenia_api_env_primaryscreen',
        screens: 'sirenia_api_env_screens'
      }
    }
    if (node.object && node.object.type === 'Identifier') {
      // Fields["xx"] -> auto field block
      if (node.object.name === 'Fields' && node.property.type === 'Literal') {
        return `<block type="sirenia_api_autofield"><field name="fieldName">${node.property.value}</field></block>`
      }
      // Inputs["xx"] -> flow input block
      if (node.object.name === 'Inputs' && node.property.type === 'Literal') {
        return `<block type="sirenia_api_flow_input"><field name="INPUT_NAME">${node.property.value}</field></block>`
      }
      // Api property accessor handling (eg Fs.tmpFolder)
      const blockName =
        node.property.type === 'Identifier' &&
        map[node.object.name] &&
        map[node.object.name][node.property.name]
      if (blockName) {
        return `<block type="${blockName}"></block>`
      }
    }
  })
  registerInterceptor('NewExpression', (code, node, walk) => {
    // 'new Field(..)' -> dynamic field block
    if (node.callee.name === 'Field' && node.arguments.length === 1) {
      const argumentBlock = walk.expr(code, node.arguments[0])
      return `<block type="sirenia_api_dynamic_field"><value name="fieldPath">${argumentBlock}</value></block>`
    }
  })

  function literalValueFromObj(optionProps, propName) {
    const prop = _.find(optionProps, p => (p.key.name || p.key.value) === propName)
    return prop ? prop.value.value : null
  }
  function makeField(optionProps, propName, fieldName, mapFn = _.identity) {
    return `<field name="${fieldName}">${mapFn(literalValueFromObj(optionProps, propName))}</field>`
  }
  function maybeStatementifyExression(exprBlock, shouldBeStatement) {
    if (!shouldBeStatement) return exprBlock
    return `<block type="javascript_expression_statement"><value name="ignoredInput">${exprBlock}</value></block>`
  }

  // Traverse the tree along the path given by the tokens
  // Return null if the path doesn't match anything - otherwise the value matched by the last token
  // A token is an array of [<property name>, <property type>, <predicate>] where predicate is used when the property points to an array
  // Both type and predicate can be omitted
  function tryGetNodeVal(node, tokens) {
    if (node == null || tokens == null || !tokens.length) return null
    const [key, type, predicate] = _.head(tokens)
    let next = node[key]
    if (_.isArray(next) && predicate) {
      next = _.find(next, predicate)
    }
    if (next == null || (type && next.type !== type)) return null
    if (tokens.length === 1) {
      return next
    }
    return tryGetNodeVal(next, _.tail(tokens))
  }

  registerInterceptor('CallExpression', (code, node, walk, isStatement) => {
    // For foo.bar() scenarios, check for high level block matches
    if (node.callee.type === 'MemberExpression') {
      let object = node.callee.object.name
      let method = node.callee.property.name || node.callee.property.value

      let argumentBlocks = node.arguments.map(a => walk.expr(code, a))
      const tryGetArg = idx => (argumentBlocks.length > idx ? argumentBlocks[idx] : '')
      const getOptionProps = argIdx =>
        node.arguments.length > argIdx && node.arguments[argIdx].type === 'ObjectExpression'
          ? node.arguments[argIdx].properties
          : []
      if (object === 'window' && method === 'alert') {
        return `<block type="text_print"><value name="TEXT">${argumentBlocks.join(
          ''
        )}</value></block>`
      }
      if (object === 'Guid' && method === 'get') {
        return `<block type="sirenia_api_guid_get"></block>`
      }
      // Debug.xx
      if (object === 'Debug') {
        if (method === 'showDialog') {
          return `<block type="sirenia_api_debug_dialog"><value name="MSG">${argumentBlocks.join(
            ''
          )}</value></block>`
        } else if (method === 'attach' || method === 'inspect' || method === 'ger') {
          return `<block type="sirenia_api_debugger"><value name="VALUE">${argumentBlocks.join(
            ''
          )}</value></block>`
        }
        return ''
      }
      // Window.xx
      if (object === 'Window') {
        if (method === 'sendKeys') {
          return `<block type="sirenia_api_window_sendkeys"><value name="KEYS">${argumentBlocks.join(
            ''
          )}</value></block>`
        }
        if (method === 'title') {
          return `<block type="sirenia_api_window_get_title"></block>`
        }
        if (method === 'minimize') {
          return `<block type="sirenia_api_window_minimize"></block>`
        }
        if (method === 'isMinimized') {
          return `<block type="sirenia_api_window_is_minimized"></block>`
        }
        if (method === 'maximize') {
          return `<block type="sirenia_api_window_maximize"></block>`
        }
        if (method === 'isMaximized') {
          return `<block type="sirenia_api_window_is_maximized"></block>`
        }
        if (method === 'focus') {
          return `<block type="sirenia_api_window_focus"></block>`
        }
        if (method === 'restore') {
          return `<block type="sirenia_api_window_restore"></block>`
        }
        if (method === 'modalShown') {
          return `<block type="sirenia_api_window_is_modal_shown"></block>`
        }
        if (method === 'withTitleShown') {
          return `<block type="sirenia_api_window_is_title_shown"><value name="TITLE">${tryGetArg(
            0
          )}</value></block>`
        }
        if (method === 'dim') {
          const amountLiteralValue =
            node.arguments && node.arguments.length > 0 && node.arguments[0].type === 'Literal'
              ? node.arguments[0].value
              : 100
          return `<block type="sirenia_api_window_dim"><field name="AMOUNT">${amountLiteralValue}</field></block>`
        }
        return ''
      }
      // Dialog.xx
      if (object === 'Dialog') {
        if (
          method === 'warn' ||
          method === 'info' ||
          method === 'input' ||
          method === 'inputHtml'
        ) {
          const headerBlock = argumentBlocks.length > 0 ? argumentBlocks[0] : ''
          const textBlock = argumentBlocks.length > 1 ? argumentBlocks[1] : ''
          const optionProps = getOptionProps(2)
          const getOptionInfo = omittedPropNames =>
            getChosenPropsMutationXml(code, optionProps, walk, omittedPropNames)
          if (method === 'input') {
            // This one is tricky because the options object contains static properties as well as dynamic properties denoting inputs
            const groupedProperties = _.groupBy(optionProps, p =>
              _.includes(
                ['sound', 'buttons', 'timeout', 'maxDialogHeight', 'maxDialogWidth'],
                p.key.name || p.key.value
              )
            ) // split them into standard properties and input properties
            const inputProperties = groupedProperties['false'] || []
            const stdProperties = groupedProperties['true'] || []
            let optionInfo = getChosenPropsMutationXml(code, stdProperties, walk) // handle mutations
            return maybeStatementifyExression(
              [
                `<block type="sirenia_api_dialog_input">`,
                optionInfo.mutationElt || '',
                `<value name="HEADER">${headerBlock}</value>`,
                `<value name="TEXT">${textBlock}</value>`,
                optionInfo ? optionInfo.propElts.join('') : '',
                `<value name="INPUTS">${walk.expr(code, {
                  type: 'ObjectExpression',
                  properties: inputProperties
                })}</value>`, // Construct a node for the Object literal containing the input properties
                `</block>`
              ].join(''),
              isStatement
            )
          }
          if (method === 'inputHtml') {
            const sourceProp = _.find(optionProps, p => (p.key.name || p.key.value) === 'source')
            let sourceBlock = sourceProp
              ? `<value name="SOURCE">${walk.expr(code, sourceProp.value)}</value>`
              : ''
            return maybeStatementifyExression(
              [
                `<block type="sirenia_api_dialog_html_input">`,
                makeField(optionProps, 'embed', 'EMBED', x => (x ? 'TRUE' : 'FALSE')),
                makeField(optionProps, 'maxDialogWidth', 'MAX_WIDTH'),
                makeField(optionProps, 'maxDialogHeight', 'MAX_HEIGHT'),
                `<value name="HEADER">${headerBlock}</value>`,
                `<value name="TEXT">${textBlock}</value>`,
                sourceBlock,
                `</block>`
              ].join(''),
              isStatement
            )
          }
          let optionInfo = getOptionInfo()
          return maybeStatementifyExression(
            `<block type="sirenia_api_dialog_info">${optionInfo.mutationElt ||
              ''}<field name="SEVERITY">${method.toUpperCase()}</field><value name="HEADER">${headerBlock}</value><value name="TEXT">${textBlock}</value>${
              optionInfo ? optionInfo.propElts.join('') : ''
            }</block>`,
            isStatement
          )
        }
        return ''
      }
      if (object === 'JSON') {
        if (method === 'parse') {
          return `<block type="javascript_json_parse"><value name="JSON">${
            argumentBlocks.length > 0 ? argumentBlocks[0] : ''
          }</value></block>`
        }
        if (method === 'stringify') {
          return `<block type="javascript_json_stringify"><value name="OBJECT">${
            argumentBlocks.length > 0 ? argumentBlocks[0] : ''
          }</value></block>`
        }
        return ''
      }
      // Flow.xx
      if (object === 'Flow') {
        if (method === 'run') {
          const argProps = getOptionProps(1)
          const argAttrs = argProps.map((p, idx) => `arg${idx}="${p.key.name || p.key.value}|"`) // Can't really provide type checking info here as javascript doesn't have it
          const argBlocks = argProps.map((p, idx) => {
            return `<value name="ARG${idx}">${walk.expr(code, p.value)}</value>`
          })
          const mutationElt = `<mutation ${argAttrs.join(' ')}></mutation>`
          const flowName =
            node.arguments.length > 0 && node.arguments[0].type === 'Literal'
              ? node.arguments[0].value
              : ''
          if (flowName) {
            return maybeStatementifyExression(
              `<block type="sirenia_api_flow_run">${mutationElt}<field name="FLOW_NAME">${flowName}</field>${argBlocks.join(
                ''
              )}</block>`,
              isStatement
            )
          }
        }
        if (
          method === 'include' &&
          node.arguments &&
          node.arguments.length === 1 &&
          node.arguments[0].type === 'Literal'
        ) {
          return `<block type="sirenia_api_flow_include"><field name="MODULE_NAME">${node.arguments[0].value}</field></block>`
        }
        return ''
      }
      // Xml.xx
      if (object === 'Xml') {
        if (method === 'load' && argumentBlocks.length === 1) {
          return `<block type="sirenia_api_xml_load"><value name="XML_TEXT">${
            argumentBlocks[0]
          }</value></block>`
        }
        if (method === 'loadFrom' && argumentBlocks.length === 1) {
          return `<block type="sirenia_api_xml_load_from"><value name="XML_URL">${
            argumentBlocks[0]
          }</value></block>`
        }
        return ''
      }
      // Http.xx
      if (object === 'Http') {
        const urlBlock = argumentBlocks.length > 0 ? argumentBlocks[0] : ''
        if (method === 'get' || method === 'delete') {
          const optionInfo = getChosenPropsMutationXml(code, getOptionProps(1), walk)
          return `<block type="sirenia_api_http_${method}">${
            optionInfo.mutationElt
          }<value name="URL">${urlBlock}</value>${optionInfo.propElts.join('')}</block>`
        }
        if (method === 'put' || method === 'post') {
          const valueBlock = argumentBlocks.length > 1 ? argumentBlocks[1] : ''
          const optionInfo = getChosenPropsMutationXml(code, getOptionProps(2), walk)
          return `<block type="sirenia_api_http_${method}">${
            optionInfo.mutationElt
          }<value name="URL">${urlBlock}</value><value name="DATA">${valueBlock}</value>${optionInfo.propElts.join(
            ''
          )}</block>`
        }
        return ''
      }
      // Ftp.xx
      if (object === 'Ftp') {
        const urlBlock = argumentBlocks.length > 0 ? argumentBlocks[0] : ''
        if (method === 'read') {
          const optionInfo = getChosenPropsMutationXml(code, getOptionProps(1), walk)
          return `<block type="sirenia_api_ftp_read">${
            optionInfo.mutationElt
          }<value name="URL">${urlBlock}</value>${optionInfo.propElts.join('')}</block>`
        }
        if (method === 'write') {
          const valueBlock = argumentBlocks.length > 1 ? argumentBlocks[1] : ''
          const optionInfo = getChosenPropsMutationXml(code, getOptionProps(2), walk)
          return `<block type="sirenia_api_ftp_write">${
            optionInfo.mutationElt
          }<value name="URL">${urlBlock}</value><value name="DATA">${valueBlock}</value>${optionInfo.propElts.join(
            ''
          )}</block>`
        }
        return ''
      }
      // App.xx
      if (object === 'App') {
        if (method === 'location') {
          return `<block type="sirenia_api_app_location"></block>`
        }
        if (method === 'navigate') {
          return `<block type="sirenia_api_app_navigate"><value name="URL">${tryGetArg(
            0
          )}</value></block>`
        }
        if (method === 'session') {
          return `<block type="sirenia_api_app_session"></block>`
        }
        if (method === 'quit') {
          return `<block type="sirenia_api_app_quit"></block>`
        }
      }
      // Excel.xx
      if (object === 'Excel') {
        if (method === 'load') {
          // { table { ... }} - Extract the properties inside the table object
          const tableOptionProperties = tryGetNodeVal(node, [
            ['arguments', 'ObjectExpression', (a, idx) => idx === 1], // second argument to Excel.load
            ['properties', 'Property', p => p.key.name === 'table' || p.key.value === 'table'], // property inside object with key 'table'
            ['value', 'ObjectExpression'], // value of that property
            ['properties'] // properties of that value
          ])
          const optionInfo = getChosenPropsMutationXml(code, tableOptionProperties, walk)
          return `<block type="sirenia_api_excel_load">${
            optionInfo.mutationElt
          }<value name="PATH">${tryGetArg(0)}</value>${optionInfo.propElts.join('')}</block>`
        }
        if (method === 'deleteSheet') {
          return `<block type="sirenia_api_excel_delete_sheet"><value name="SHEET">${tryGetArg(
            0
          )}</value><value name="PATH">${tryGetArg(1)}</value></block>`
        }
        if (method === 'updateCell') {
          return [
            `<block type="sirenia_api_excel_updatecell">`,
            `<value name="PATH">${tryGetArg(0)}</value>`,
            `<value name="SHEET">${tryGetArg(1)}</value>`,
            `<value name="CELL">${tryGetArg(2)}</value>`,
            `<value name="VALUE">${tryGetArg(3)}</value>`,
            `</block>`
          ].join('')
        }
        if (method === 'updateCells') {
          return [
            `<block type="sirenia_api_excel_updatecells">`,
            `<value name="PATH">${tryGetArg(0)}</value>`,
            `<value name="SHEET">${tryGetArg(1)}</value>`,
            `<value name="CELL">${tryGetArg(2)}</value>`,
            `<value name="DATA">${tryGetArg(3)}</value>`,
            `</block>`
          ].join('')
        }
      }
      // Fs.xx
      if (object === 'Fs') {
        if (method === 'ls') {
          // Detect specifically `{ deepMatch: true }` as second argument
          const deep = tryGetNodeVal(node, [
            ['arguments', 'ObjectExpression', (a, idx) => idx === 1],
            [
              'properties',
              'Property',
              p => p.key.name === 'deepMatch' || p.key.value === 'deepMatch'
            ],
            ['value', 'Literal']
          ])
          return `<block type="sirenia_api_fs_ls"><field name="SHALLOW">${
            deep ? 'DEEP' : 'SHALLOW'
          }</field><value name="PATH">${tryGetArg(0)}</value></block>`
        }
        if (method === 'write') {
          return `<block type="sirenia_api_fs_write"><value name="PATH">${tryGetArg(
            0
          )}</value><value name="TEXT">${tryGetArg(1)}</value></block>`
        }
        if (method === 'tmpFile') {
          return `<block type="sirenia_api_fs_tmpfile"></block>`
        }
      }
      // Notification.xx
      if (object === 'Notification') {
        if (method === 'show' || method === 'update') {
          const optionProps = getOptionProps(3)
          const options = getChosenPropsMutationXml(code, optionProps, walk, ['severity']) // Omit the 'severity' value as it is a static property of the block
          const severityVal =
            tryGetNodeVal(node, [
              ['arguments', 'ObjectExpression', (a, idx) => idx === 3],
              [
                'properties',
                'Property',
                p => p.key.name === 'severity' || p.key.value === 'severity'
              ],
              ['value', 'Literal'],
              ['value']
            ]) || 'INFO'
          return [
            `<block type="sirenia_api_notifications_${method}">${options.mutationElt}`,
            `<field name="SEVERITY">${severityVal}</field>`,
            `<value name="NAME">${tryGetArg(0)}</value>`,
            `<value name="HEADER">${tryGetArg(1)}</value>`,
            `<value name="MESSAGE">${tryGetArg(2)}</value>`,
            options.propElts.join(''),
            `</block>`
          ].join('')
        }
        if (method === 'close') {
          return `<block type="sirenia_api_notifications_close"><value name="NAME">${tryGetArg(
            0
          )}</value></block>`
        }
      }
      // Processes.xx
      if (object === 'Processes') {
        if (method === 'all') {
          return `<block type="sirenia_api_processes_all"></block>`
        }
        if (method === 'spawn') {
          return `<block type="sirenia_api_processes_spawn"><value name="CMD">${tryGetArg(
            0
          )}</value></block>`
        }
      }
      // Timer.xx
      if (object === 'Timer') {
        if (method === 'start') {
          return `<block type="sirenia_api_timer_start"><value name="NAME">${tryGetArg(
            0
          )}</value></block>`
        }
        if (method === 'log') {
          return `<block type="sirenia_api_timer_log"><value name="NAME">${tryGetArg(
            0
          )}</value><value name="MESSAGE">${tryGetArg(1)}</value></block>`
        }
        if (method === 'stop') {
          return `<block type="sirenia_api_timer_stop"><value name="NAME">${tryGetArg(
            0
          )}</value></block>`
        }
      }
      if (object === 'Wait') {
        const waitUnit =
          method === 'forSeconds' ? 'seconds' : method === 'forMilliseconds' ? 'milliseconds' : null
        if (waitUnit) {
          const durationArgValue =
            node.arguments.length > 0 &&
            node.arguments[0].type === 'Literal' &&
            node.arguments[0].value
          return `<block type="sirenia_api_wait"><field name="UNIT">${waitUnit}</field><field name="DURATION">${durationArgValue}</field></block>`
        }
        if (method === 'forField') {
          const timeoutArgValue =
            node.arguments.length > 1 &&
            node.arguments[1].type === 'Literal' &&
            node.arguments[1].value
          return `<block type="sirenia_api_wait_field"><value name="field">${tryGetArg(
            0
          )}</value><field name="waitType">appear</field><field name="timeoutSeconds">${timeoutArgValue}</field></block>`
        }
        if (method === 'forFieldToDisappear') {
          const timeoutArgValue =
            node.arguments.length > 1 &&
            node.arguments[1].type === 'Literal' &&
            node.arguments[1].value
          return `<block type="sirenia_api_wait_field"><value name="field">${tryGetArg(
            0
          )}</value><field name="waitType">disappear</field><field name="timeoutSeconds">${timeoutArgValue}</field></block>`
        }
      }
      // Windows.xx
      if (object === 'Windows') {
        if (method === 'all') {
          return `<block type="sirenia_api_windows_all"></block>`
        }
        if (method === 'forApp') {
          return `<block type="sirenia_api_windows_for_app"></block>`
        }
      }

      // TODO: support more API methods

      // Handle 'foo.bar(..)' cases as they will often be API field method invocations
      let fieldBlock = walk.expr(code, node.callee.object)
      // xx.select
      if (method === 'select' && node.arguments && node.arguments.length > 0) {
        const optionProps = getOptionProps(1)
        let optionsInfo = getChosenPropsMutationXml(code, optionProps, walk)
        const valueBlock = walk.expr(code, node.arguments[0])
        return `<block type="sirenia_api_field_select">${
          optionsInfo.mutationElt
        }<value name="VALUE">${valueBlock}</value><value name="FIELD">${fieldBlock}</value>${optionsInfo.propElts.join(
          ''
        )}</block>`
      }
      // xx.selectIndex
      if (method === 'selectIndex' && node.arguments && node.arguments.length > 0) {
        const indexBlock = walk.expr(code, node.arguments[0])
        return `<block type="sirenia_api_field_select_index"><value name="INDEX">${indexBlock}</value><value name="FIELD">${fieldBlock}</value></block>`
      }
      // xx.input
      if (method === 'input' && node.arguments && node.arguments.length > 0) {
        const valueBlock = walk.expr(code, node.arguments[0])
        return `<block type="sirenia_api_field_input"><value name="VALUE">${valueBlock}</value><value name="FIELD">${fieldBlock}</value></block>`
      }
      // xx.inputNative
      if (method === 'inputNative' && node.arguments && node.arguments.length > 0) {
        const valueBlock = walk.expr(code, node.arguments[0])
        let delay = ''
        if (node.arguments.length > 1 && node.arguments[1].type === 'Literal') {
          delay = node.arguments[1].value
        }
        return `<block type="sirenia_api_field_input_native">${
          delay ? `<mutation chosenProps="delay"></mutation>` : ''
        }<value name="VALUE">${valueBlock}</value><value name="FIELD">${fieldBlock}</value>${
          delay
            ? `<value name="delay"><block type="math_number"><field name="NUM">${delay}</field></block></value>`
            : ''
        }</block>`
      }
      // xx.click(..)
      var clickKind =
        method === 'click'
          ? 'LEFT'
          : method === 'rightClick'
          ? 'RIGHT'
          : method === 'doubleClick'
          ? 'DOUBLE'
          : null
      if (clickKind != null) {
        let optionsInfo = getChosenPropsMutationXml(code, getOptionProps(0), walk)
        return `<block type="sirenia_api_field_click">${
          optionsInfo.mutationElt
        }<field name="CLICK_KIND">${clickKind}</field><value name="field">${fieldBlock}</value>${optionsInfo.propElts.join(
          ''
        )}</block>`
      }
      if (method === 'simulateClick') {
        return `<block type="sirenia_api_field_simulate_click"><value name="field">${fieldBlock}</value></block>`
      }
      // xx.editCell(..)
      if (method === 'editCell') {
        const rowBlock = node.arguments.length > 0 ? walk.expr(code, node.arguments[0]) : ''
        const colBlock = node.arguments.length > 1 ? walk.expr(code, node.arguments[1]) : ''
        const valBlock = node.arguments.length > 2 ? walk.expr(code, node.arguments[2]) : ''
        const optionsInfo = getChosenPropsMutationXml(code, getOptionProps(3), walk)
        return `<block type="sirenia_api_edit_cell">${
          optionsInfo.mutationElt
        }<value name="rowMatch">${rowBlock}</value><value name="colMatch">${colBlock}</value><value name="value">${valBlock}</value><value name="tableField">${fieldBlock}</value>${optionsInfo.propElts.join(
          ''
        )}</block>`
      }
      // xx.clickCell(..)
      if (method === 'clickCell') {
        const rowBlock = node.arguments.length > 0 ? walk.expr(code, node.arguments[0]) : ''
        const colBlock = node.arguments.length > 1 ? walk.expr(code, node.arguments[1]) : ''
        const optionsInfo = getChosenPropsMutationXml(code, getOptionProps(2), walk)
        return `<block type="sirenia_api_click_cell">${
          optionsInfo.mutationElt
        }<value name="rowMatch">${rowBlock}</value><value name="colMatch">${colBlock}</value><value name="tableField">${fieldBlock}</value>${optionsInfo.propElts.join(
          ''
        )}</block>`
      }
      // xx.exists()
      if (method === 'exists') {
        return maybeStatementifyExression(
          `<block type="sirenia_api_field_exists"><value name="FIELD">${fieldBlock}</value></block>`,
          isStatement
        )
      }
      // xx.read()
      if (method === 'read') {
        return maybeStatementifyExression(
          `<block type="sirenia_api_field_read"><value name="field">${fieldBlock}</value></block>`,
          isStatement
        )
      }
      // xx.highlight / xx.highlightWithColor
      if (method === 'highlight' || method === 'highlightWithColor') {
        const color =
          method === 'highlight' || node.arguments.length === 0
            ? 'RED'
            : node.arguments[0].type === 'Literal' && node.arguments[0].value
            ? node.arguments[0].value.toUpperCase()
            : 'RED'
        return `<block type="sirenia_api_field_highlight">
          <field name="COLOR">${color}</field>
          <value name="FIELD">${fieldBlock}</value>
        </block>`
      }
      // xx.lowlight
      if (method === 'lowlight') {
        return `<block type="sirenia_api_field_lowlight">
          <value name="FIELD">${fieldBlock}</value>
        </block>`
      }
    }
  })
}
